import { useState, type FormEvent } from "react";
import { Check, Loader2 } from "lucide-react";
import { CarrotIcon } from "@/components/CarrotIcon";
import { MAILTO, REGINA_EMAIL } from "@/lib/site";

type Status = "idle" | "sending" | "sent" | "error";

const fieldClass =
  "w-full rounded-xl border border-ink/15 bg-paper px-4 py-3 text-base text-ink placeholder:text-ink/50 outline-none transition focus:border-carrot focus:ring-2 focus:ring-carrot/25";

const labelClass = "mb-1.5 block text-sm font-semibold text-ink";

/**
 * Contact form wired to POST /api/contact. Style-neutral for a light surface
 * (used inside the contact dialog). Calls onSuccess after a successful send.
 */
export function ContactForm({ onSuccess }: { onSuccess?: () => void }) {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "sending") return;

    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());

    setStatus("sending");
    setError(null);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(payload?.error ?? "Something went wrong.");
      }
      setStatus("sent");
      form.reset();
      onSuccess?.();
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  if (status === "sent") {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-carrot text-primary-foreground">
          <Check className="size-7" />
        </span>
        <p className="text-xl font-extrabold text-ink">Thanks! Message received.</p>
        <p className="text-ink/60">We'll be in touch shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="text-left">
      {/* Honeypot: hidden from real users, catches naive bots. */}
      <div aria-hidden className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label>
          Company
          <input type="text" name="company" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className={labelClass}>
            Name
          </label>
          <input
            id="contact-name"
            name="name"
            required
            maxLength={120}
            placeholder="Your name"
            autoComplete="name"
            className={fieldClass}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className={labelClass}>
            Email
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            maxLength={254}
            placeholder="you@example.com"
            autoComplete="email"
            className={fieldClass}
          />
        </div>
      </div>
      <div className="mt-3">
        <label htmlFor="contact-role" className={labelClass}>
          Your role <span className="font-normal text-ink/60">(optional)</span>
        </label>
        <input
          id="contact-role"
          name="role"
          maxLength={120}
          placeholder="Rancher, trainer, investor…"
          className={fieldClass}
        />
      </div>
      <div className="mt-3">
        <label htmlFor="contact-message" className={labelClass}>
          Message
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={5000}
          rows={4}
          placeholder="Tell us a little about you and what you're after."
          className={`${fieldClass} resize-y`}
        />
      </div>

      {status === "error" && error ? <p className="mt-3 text-sm font-medium text-destructive">{error}</p> : null}

      <button
        type="submit"
        disabled={status === "sending"}
        className="action-button group mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-bold disabled:cursor-not-allowed disabled:opacity-70"
      >
        {status === "sending" ? (
          <>
            Sending
            <Loader2 className="size-5 animate-spin" />
          </>
        ) : (
          <>
            Send message
            <span className="flex size-8 items-center justify-center rounded-full bg-ink/10">
              <CarrotIcon
                className="h-5 w-4 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:translate-x-0.5"
              />
            </span>
          </>
        )}
      </button>

      <p className="mt-4 text-center text-sm text-ink/55">
        Or email us at{" "}
        <a href={MAILTO} className="font-semibold text-field-deep underline underline-offset-2">
          {REGINA_EMAIL}
        </a>
      </p>
    </form>
  );
}
